import React, { useEffect, useRef, useState } from 'react'
import Container from '../../../../../elements/Container'
import P from '../../../../../elements/Typo/P'
import Button from '../../../../../elements/button'

import { useExperimentApi } from '../../../../../store/api/hooks/experimentApiHook'
import { useNavigate, useParams } from 'react-router-dom'
import { URLS } from '../../../../../_config'
import DataSelection from './dataSelection'
import { useExperimentSelector } from '../../../../../store/selectors/experimentSelector'
import toast from 'react-hot-toast'
import DataFileUploader from '../../../../../components/DataFileUploader'
import { SelectedDataTableType, UploadStatusType } from '../../../../../_config/types'
import { Tooltip } from 'react-daisyui'
import Icon from '../../../../../components/icons'
import { trackEvent } from '../../../../../_common/functions'
import Loader from '../../../../../components/loader'
import { useTranslation } from 'react-i18next'

interface UploadDataProps {
    experimentLiteral?: string
    onComplete?: (message: string) => void
    hidePreviousStep?: boolean
}

const UploadData: React.FC<UploadDataProps> = ({ experimentLiteral, onComplete, hidePreviousStep }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const params = useParams()
    const experimentApi = useExperimentApi()
    const reduxExperiment = useExperimentSelector()
    const [fetchedExperiment, setFetchedExperiment] = useState<any>(null)

    const literal = experimentLiteral || params.experiment
    const experiment = experimentLiteral ? fetchedExperiment : reduxExperiment

    useEffect(() => {
        if (experimentLiteral) {
            experimentApi.getExperimentDetailsWatch(
                { literal: experimentLiteral + '?use_orm=true' },
                (resp: any) => {
                    setFetchedExperiment(resp)
                },
                (error: any) => {
                    toast.error(error?.message || 'Failed to fetch experiment details')
                },
            )
        }
    }, [experimentLiteral])
    const [FileUploader, setFileUploader] = useState<UploadStatusType>('ready-for-upload')
    const [selectedDataTable, setSelectedDataTable] = useState<SelectedDataTableType | null>(null)
    const [isFileChanged, setIsFileChanged] = useState<boolean>(false)
    const [working, setWorking] = useState<boolean>(false)
    const dataSelectionRef: any = useRef()

    const GoWithUploadedFile = () => {
        if (experiment?.data_mappings?.user_created_table_name == null && !experimentLiteral) {
            toast.error(t('Please Upload any files or select data from existing table'))
            return
        }
        const paramsData: any = {
            step: Math.max(experiment.step, 2),
            is_draft: true,
            literal,
        }

        if (isFileChanged) {
            paramsData.step = 2
        }
        experimentApi.updateExperiment(
            paramsData,
            (data: any) => {
                trackEvent('wizard_step2_completed', { experiment_id: experiment?.experiment_literal })
                if (isFileChanged) {
                    GoNext('?step=2')
                } else {
                    GoNext()
                }
            },
            (error: any) => {
                trackEvent('wizard_step_api_error', { step: 2, error_code: error?.message })
                toast.error(error?.message)
            },
        )
    }

    const GenerateDataQualityReport = () => {
        setWorking(true)
        experimentApi.generateDataQualityReport(
            {
                literal,
                table_name: selectedDataTable?.table.name,
                upload_type: selectedDataTable?.type,
            },
            (data: any) => {
                setWorking(false)
                GoNext()
            },
            (error: any) => {
                setWorking(false)
                trackEvent('wizard_step_api_error', { step: 2, error_code: error?.message })
                toast.error(error?.message)
            },
        )
    }

    const GoWithSelectedTable = () => {
        const payload: any = {
            step: Math.max(experiment.step, 2),
            is_draft: true,
            literal,
        }

        let changed: boolean = false

        if (selectedDataTable?.type == 'user') {
            if (experiment.step > 1) {
                if (experiment?.data_mappings?.delta_design_table_name || selectedDataTable.table.user_design_literal != experiment?.user_design?.table_name) {
                    changed = true
                }
            }
            payload.user_design_id = selectedDataTable.table.user_design_literal
        } else {
            if (experiment.step > 1) {
                if (experiment?.user_design?.table_name || selectedDataTable?.table.name != experiment?.data_mappings?.delta_design_table_name) {
                    changed = true
                }
            }
            payload.design_table_name = selectedDataTable?.table.name
        }

        if (changed) {
            payload.step = 2
        }

        experimentApi.updateExperiment(
            payload,
            (data: any) => {
                GenerateDataQualityReport()
            },
            (error: any) => {
                trackEvent('wizard_step_api_error', { step: 2, error_code: error?.message })
                toast.error(error?.message)
            },
        )
    }

    const GoNext = (step: string = '') => {
        if (onComplete) {
            onComplete('I have uploaded the file')
        } else {
            void navigate(URLS.CREATE_EXPERIMENT(params.experiment) + step)
        }
    }

    const updateExperiment = () => {
        if (selectedDataTable) {
            GoWithSelectedTable()
        } else if (FileUploader == 'data-ready') {
            GoWithUploadedFile()
        }
    }

    if (experimentLiteral && !fetchedExperiment) {
        return (
            <Container size="md" className="relative">
                <div className="flex justify-center items-center py-10">
                    <Loader />
                </div>
            </Container>
        )
    }

    return (
        <Container size="md" className="relative">
            {working && (
                <div className="flex justify-center items-center absolute z-10 top-0 left-0 w-full h-full bg-white bg-opacity-50">
                    <Loader />
                </div>
            )}
            <div className="flex items-center justify-between gap-2 p-1.5">
                {/* ${selectedDataTable ? 'opacity-50 cursor-pointer' : ''} */}
                <div className={`w-[44%]  flex-shrink-0 my-4 `}>
                    <div className="flex items-center">
                        <P className="text-[#414040] !mb-2 mt-2">{t('Upload your own data')}</P>
                        <Tooltip message={t('Upload your own data')} color="info" position="top" className="ml-1">
                            <Icon name="outlineInfoCircle" size={'0.6'} />
                        </Tooltip>
                    </div>

                    <DataFileUploader isFileChanged={setIsFileChanged} uploadType="design" literal={literal as string} uploadingStatus={setFileUploader} experimentData={experimentLiteral ? experiment : undefined} />
                </div>

                <div className="flex items-center justify-center leading-none text-[0.667rem] font-semibold w-[1.833rem] h-[1.833rem] bg-[#F4F4F4] border border-dashed border-[#BEBEBE] rounded-full">{t('OR')}</div>

                <div className="w-[44%] flex-shrink-0 my-4">
                    <DataSelection _ref={dataSelectionRef} _for="experiment" selectedDataTable={setSelectedDataTable} experimentData={experimentLiteral ? experiment : undefined} />
                </div>
            </div>
            <div className="flex items-center justify-between mt-4 mb-2 ">
                <div className="flex items-center gap-3">
                    {!hidePreviousStep && (
                        <Button
                            theme="secondary"
                            styleType="outline"
                            size="xs"
                            onClick={() => {
                                trackEvent('wizard_back_clicked', { for: params.experiment })
                                void navigate(URLS.CREATE_EXPERIMENT(params.experiment) + '?step=0')
                            }}
                        >
                            {t('Previous Step')}
                        </Button>
                    )}
                    {/* <Button theme="secondary" styleType="outline" size="xs">
                        <Icon name="save" />
                        Save as Draft
                    </Button> */}
                </div>
                <div className="flex items-center gap-3 px-2">
                    <Button
                        theme="secondary"
                        size="xs"
                        className="text-white"
                        disabled={!(selectedDataTable != null || FileUploader == 'data-ready') || working}
                        onClick={() => {
                            trackEvent('wizard_next_clicked', { timestamp: new Date().toISOString(), table_name: selectedDataTable?.table?.name, step: experiment?.step })
                            updateExperiment()
                        }}
                    >
                        {working ? <Loader /> : t('NEXT STEP')}
                    </Button>
                </div>
            </div>

            {working && <div className="text-end text-yellow-600 font-medium text-[0.65rem] italic">{t('It may take a few minutes to process your data. It depends on your table size, Please do not close or refresh the page.')}</div>}

            <div className="flex gap-2 items-center">
                {/* <Icon name="envelop" security="0.7rem" />
                <Text size="xs">
                    Having trouble submitting your information? If your <b>data</b> is <b>too large</b>, please reach out to us directly via <b>email</b>
                </Text> */}
            </div>
        </Container>
    )
}

export default UploadData
